/*
 * ds-cart-sync — an extra-placement fee belongs to its garment, not to the cart.
 * ---------------------------------------------------------------------------
 * WHY THIS FILE EXISTS. Extra print placements are charged on their own cart
 * lines, because a non-Plus store cannot make a cart line cost more than
 * `variant price x quantity` (see CLAUDE.md — Cart Transform is Plus-only, do
 * not re-investigate). Shopify has no notion of a child line, so those fee
 * lines are ordinary lines that happen to be hidden by the fold in
 * snippets/cart-products.liquid. Remove the garment and the fees stayed:
 *
 *   • delete a polo carrying two extra prints  -> two "Extra Print Placement"
 *     rows are left behind, and because the cart no longer adds up the fold
 *     switches off and UNHIDES them. The shopper is looking at a cart holding
 *     nothing but fees for a garment they just deleted.
 *   • drop a polo from quantity 2 to 1         -> the fee line stays at 2.
 *
 * Both were reported from a real cart on 2026-09-11.
 *
 * WHAT IT DOES. After anything touches the cart, it recomputes what each
 * surcharge variant SHOULD total — `Σ (garment quantity x per-garment count)`,
 * read from the `_ds_surcharge_lines` binding the studio writes onto the
 * garment line — and pins the fee lines to it in one /cart/update.js.
 *
 * IT NEVER RE-ADDS A DELETED LINE. Reducing or removing a fee only ever takes
 * away a charge for something that is no longer in the cart, so it can be done
 * silently. Putting a charge BACK is a different act: a shopper who deliberately
 * deleted the fee row would be in a fight with a script that keeps re-adding it.
 * That case already has a deliberate answer — the warning in
 * snippets/cart-products.liquid, with a button that says what it will cost. So:
 *
 *   line exists, quantity too high  -> lowered here, silently
 *   line exists, quantity too low   -> topped up here (the shopper raised the
 *                                      garment quantity; they never touched the
 *                                      fee row, so there is nothing to argue with)
 *   line gone entirely              -> left alone; the warning owns it
 *
 * NOT A SECURITY BOUNDARY. `_ds_surcharge_lines` is client-written, so this is
 * housekeeping, not enforcement. What is actually charged is recomputed
 * server-side from committed geometry (backend lib/checkout.js folds
 * `expectedSurchargeForLine`, and routes/orders-paid.js holds a short-paid
 * order), and this file cannot make the cart charge anything a variant does not
 * already cost.
 *
 * KNOWN LIMIT: a cart line added by a studio build older than the seam-removal
 * round carries `_pf_placements` but no `_ds_surcharge_lines`, so its fees look
 * unowned and would be cleared. The consequence is an undercharge on the native
 * checkout path only — the draft-order checkout re-prices from geometry, and
 * the webhook holds anything short — and the store is pre-launch with no such
 * carts in the wild.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var CART_EVENT = 'shopify:cart:lines-update';
  var SOURCE = 'ds-cart-sync';

  var SURCHARGE = '_ds_surcharge';        // '1' on a fee line
  var BINDING = '_ds_surcharge_lines';    // '<variantId>:<perGarment>,…' on the garment line

  var running = false;

  function props(item) {
    return (item && item.properties) || null;
  }

  function isSurchargeLine(item) {
    var p = props(item);
    return !!p && String(p[SURCHARGE]) === '1';
  }

  /** What each surcharge variant should total across the whole cart.
   *
   * Per VARIANT, not per line, and deliberately so: Shopify merges lines that
   * share a variant and properties, so a fee line cannot be attributed back to
   * one garment. snippets/cart-products.liquid compares the same way. */
  function wantedTotals(cart) {
    var wanted = Object.create(null);
    (cart.items || []).forEach(function (item) {
      if (isSurchargeLine(item)) return;
      var raw = props(item) && props(item)[BINDING];
      if (!raw) return;
      var qty = Number(item.quantity) || 0;
      String(raw).split(',').forEach(function (pair) {
        var bits = pair.split(':');
        // Digits only. A forged property can then only ever name a real variant
        // id, and Shopify charges a variant its own price either way.
        var id = String(bits[0] || '').replace(/[^0-9]/g, '');
        var per = parseInt(bits[1], 10) || 0;
        if (id && per > 0) wanted[id] = (wanted[id] || 0) + per * qty;
      });
    });
    return wanted;
  }

  /** The `{ lineKey: quantity }` changes that would pin the fees to the garments. */
  function planUpdates(cart) {
    var wanted = wantedTotals(cart);
    var byVariant = Object.create(null);
    (cart.items || []).forEach(function (item) {
      if (!isSurchargeLine(item)) return;
      var id = String(item.variant_id);
      (byVariant[id] || (byVariant[id] = [])).push(item);
    });

    var updates = {};
    Object.keys(byVariant).forEach(function (id) {
      var lines = byVariant[id];
      var target = wanted[id] || 0;
      var have = lines.reduce(function (n, l) { return n + (Number(l.quantity) || 0); }, 0);
      if (have === target) return;

      if (have > target) {
        // Take the excess off the LAST lines first, so the oldest fee line (the
        // one the fold is most likely showing against a garment) is the survivor.
        var excess = have - target;
        for (var i = lines.length - 1; i >= 0 && excess > 0; i--) {
          var q = Number(lines[i].quantity) || 0;
          var cut = Math.min(q, excess);
          updates[lines[i].key] = q - cut;
          excess -= cut;
        }
      } else {
        // Short, but the line is still there — the shopper raised the garment
        // quantity and never touched this row. Top the first line up. A variant
        // with NO line left is not in `byVariant` at all, so it is never re-added.
        updates[lines[0].key] = (Number(lines[0].quantity) || 0) + (target - have);
      }
    });
    return updates;
  }

  /** Cart section ids on this page, so the theme can morph instead of reload. */
  function sectionIds() {
    return Array.prototype.slice
      .call(document.querySelectorAll('cart-items-component'))
      .map(function (el) { return el.dataset && el.dataset.sectionId; })
      .filter(Boolean);
  }

  /* The theme's cart contract: a `shopify:cart:lines-update` carrying a promise
   * that later resolves with the re-rendered sections. cart-items-component
   * morphs the cart from it, and mz-cart-count re-reads the badge — so nothing
   * here has to touch the DOM, and no reload closes an open drawer.
   *
   * A plain Event with the fields assigned on, not the @shopify/events class:
   * the listeners read `.target` and `.promise` and nothing else, and this file
   * stays dependency-free like mz-cart-count.js (see its header). */
  function dispatch(lines) {
    var deferred = {};
    deferred.promise = new Promise(function (resolve, reject) {
      deferred.resolve = resolve;
      deferred.reject = reject;
    });
    var event = new Event(CART_EVENT, { bubbles: true, composed: true });
    Object.assign(event, {
      action: 'update',
      context: 'cart',
      lines: lines,
      source: SOURCE,
      promise: deferred.promise
    });
    document.dispatchEvent(event);
    return deferred;
  }

  function sync() {
    if (running) return Promise.resolve();
    running = true;
    var ids = sectionIds();

    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) return null;
        var updates = planUpdates(cart);
        var keys = Object.keys(updates);
        if (!keys.length) return null;

        var deferred = dispatch(keys.map(function (k) {
          return { merchandiseId: k, quantity: updates[k] };
        }));

        var body = { updates: updates };
        if (ids.length) {
          body.sections = ids.join(',');
          body.sections_url = window.location.pathname;
        }
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        })
          .then(function (r) {
            if (!r.ok) throw new Error('cart update ' + r.status);
            return r.json();
          })
          .then(function (updated) {
            deferred.resolve({
              cart: { id: updated.token || '', totalQuantity: updated.item_count || 0, lines: [] },
              detail: {
                sections: updated.sections || null,
                items: updated.items || [],
                itemCount: updated.item_count || 0,
                source: SOURCE,
                sourceId: SOURCE,
                didError: false
              }
            });
          })
          .catch(function (error) {
            // Resolve rather than reject: a rejected promise makes the theme log
            // a warning and leaves the cart mid-shimmer. A cart we could not fix
            // is the cart the shopper already had — the fold turns itself off and
            // the existing warning explains it.
            deferred.resolve({ cart: null, detail: { didError: true, source: SOURCE } });
            throw error;
          });
      })
      .catch(function () {
        /* A cart we cannot read or write must not break the page. */
      })
      .then(function () {
        running = false;
      });
  }

  /* Run after the mutation that woke us has actually landed — /cart.js read too
   * early returns the pre-update cart and we would "fix" the wrong thing. Our
   * own event is skipped so the pass cannot chase its own tail. */
  function onCartEvent(event) {
    if (event && event.source === SOURCE) return;
    var promise = event && event.promise;
    if (promise && typeof promise.then === 'function') promise.then(sync, sync);
    else sync();
  }

  document.addEventListener(CART_EVENT, onCartEvent);
  document.addEventListener('cart:updated', onCartEvent);
  // A cart reached without JS (a form post, a back button) can be inconsistent
  // before anything fires an event, so check once on arrival too.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { sync(); });
  } else {
    sync();
  }
})();
